import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const KEY = "carspa_cookie_consent_v1";

const read = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; }
};

/** Apply the stored choice to PostHog (analytics) — marketing is recorded for compliance. */
const applyConsent = (c) => {
  const ph = window.posthog;
  if (!ph || typeof ph.opt_in_capturing !== "function") return;
  if (c?.analytics) ph.opt_in_capturing();
  else ph.opt_out_capturing();
};

const save = (analytics, marketing) => {
  const c = { analytics, marketing, ts: new Date().toISOString() };
  try { localStorage.setItem(KEY, JSON.stringify(c)); } catch { /* ignore */ }
  applyConsent(c);
  return c;
};

/** GDPR cookie banner: gates PostHog analytics + marketing until the visitor chooses. */
export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  useEffect(() => {
    const stored = read();
    if (stored) applyConsent(stored);
    else { if (window.posthog?.opt_out_capturing) window.posthog.opt_out_capturing(); setVisible(true); }
    const reopen = () => { const s = read(); setAnalytics(s?.analytics ?? true); setMarketing(s?.marketing ?? true); setShowSettings(true); setVisible(true); };
    window.addEventListener("open-cookie-settings", reopen);
    return () => window.removeEventListener("open-cookie-settings", reopen);
  }, []);

  if (!visible) return null;

  const done = () => { setVisible(false); setShowSettings(false); };
  const acceptAll = () => { save(true, true); done(); };
  const rejectAll = () => { save(false, false); done(); };
  const saveChoice = () => { save(analytics, marketing); done(); };

  const Toggle = ({ label, desc, on, set, locked, testid }) => (
    <label className="flex items-start gap-3 py-2 cursor-pointer">
      <input type="checkbox" checked={on} disabled={locked} onChange={(e) => set(e.target.checked)} data-testid={testid} className="mt-1 accent-[#00DF82] disabled:opacity-60" />
      <span><span className="text-white text-sm font-semibold">{label}</span><span className="block text-xs text-brand-muted">{desc}</span></span>
    </label>
  );

  const btn = "h-10 px-4 text-sm font-semibold transition-colors";
  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4" data-testid="cookie-consent">
      <div className="max-w-[1000px] mx-auto bg-brand-surface border border-brand-accent/40 shadow-2xl">
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <span className="w-2 h-2 bg-brand-accent mt-1.5 shrink-0" />
            <div className="flex-1">
              <div className="font-display font-black text-lg text-white">Nastavení soukromí</div>
              <p className="mt-1.5 text-sm text-brand-muted">
                Používáme soubory cookies pro provoz webu, anonymní analytiku (PostHog) a marketing. Analytiku a marketing spustíme jen s vaším souhlasem. Více v{" "}
                <Link to="/zasady-cookies" className="text-brand-accent hover:underline">zásadách cookies</Link>.
              </p>

              {showSettings && (
                <div className="mt-3 border-t border-brand-border pt-2" data-testid="cookie-settings">
                  <Toggle label="Nezbytné" desc="Vždy aktivní — zajišťují základní funkce webu." on locked testid="cookie-necessary" set={() => {}} />
                  <Toggle label="Analytické" desc="Anonymní statistiky návštěvnosti (PostHog)." on={analytics} set={setAnalytics} testid="cookie-analytics" />
                  <Toggle label="Marketingové" desc="Měření kampaní a relevantní nabídky." on={marketing} set={setMarketing} testid="cookie-marketing" />
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={acceptAll} data-testid="cookie-accept-all" className={`${btn} bg-brand-accent text-black hover:bg-brand-accentHover`}>Přijmout vše</button>
                <button onClick={rejectAll} data-testid="cookie-reject-all" className={`${btn} border border-brand-border text-white hover:border-brand-accent hover:text-brand-accent`}>Odmítnout</button>
                {showSettings
                  ? <button onClick={saveChoice} data-testid="cookie-save" className={`${btn} border border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-black`}>Uložit volbu</button>
                  : <button onClick={() => setShowSettings(true)} data-testid="cookie-settings-open" className={`${btn} border border-brand-border text-brand-muted hover:text-white hover:border-brand-accent`}>Nastavení</button>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
